import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { formatNumericValue } from 'utils/general';

import { IContracts } from '@/modules/workforce/Contracts/types';
import { IDetailsHeaderStatus } from '@/shared/components/molecules/DetailsPageHeader/DetailsPageHeader';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { getLocalizedDate } from '@/shared/lib/dates';
import { IGeneral } from '@/shared/types';

export const useContractDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [selectedAsset] = useStore((store) => store?.asset);

  const getContractStatus = (
    status: 'Active' | 'Past' | 'Upcoming'
  ): IDetailsHeaderStatus => {
    const contractStatusStyles: Record<string, IDetailsHeaderStatus> = {
      Past: {
        label: t('Past'),
        color: 'grey',
      },
      Active: {
        label: t('Active'),
        color: 'green',
      },
      Upcoming: {
        label: t('Upcoming'),
        color: 'blue',
      },
    };

    return contractStatusStyles[status];
  };

  const checkUnitMismatch = (
    contractStatus: IGeneral.IBinary,
    dailyWage: IGeneral.IBinary,
    units: IContracts.IPriceUnit[] | null
  ) => {
    if (!units || contractStatus === 0 || dailyWage === 1) return false;
    return units.filter((item) => item.price === 0).length > 0;
  };

  const getUnitsTable = () => {
    const columnHelper = createColumnHelper<IContracts.IPriceUnit>();
    return [
      columnHelper.display({
        header: t('Unit'),
        cell: ({ row }) => row.index + 1,
      }),
      columnHelper.accessor('unit', {
        header: t('Unit Type'),
        cell: ({ getValue }) => getValue() || '-',
      }),
      columnHelper.accessor('price', {
        header: t('Unit Value'),
        cell: ({ getValue }) =>
          getValue()
            ? `${formatNumericValue(getValue())}${
                selectedAsset?.currency ? ` ${t(selectedAsset.currency)}` : ''
              }`
            : '-',
      }),
      // columnHelper.accessor('totalExpectedProductivity', {
      //   header: t('Required total productivity'),
      //   cell: ({ getValue, row: { original } }) => {
      //     const value = getValue();
      //     return value ? `${formatNumericValue(value)} ${original.unit}` : '-';
      //   },
      // }),
    ];
  };

  const parseIntervalsTime = (time: string) => {
    const date = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    date.setHours(hours, minutes);
    return getLocalizedDate({
      date,
      locale: language,
      formatDate: 'h:mm a',
    });
  };

  const getWorkingHours = (intervals: IContracts.IContractInterval[]) => {
    const coreInterval = intervals.find((item) => item.type === 'core');
    return coreInterval
      ? t('from-to-hours', {
          startTime: parseIntervalsTime(coreInterval.startTime),
          endTime: parseIntervalsTime(coreInterval.endTime),
        })
      : '-';
  };

  const getOvertime = (intervals: IContracts.IContractInterval[]) => {
    const overtime = intervals
      .filter((item) => item.type === 'extra')
      .reduce((acc, curr) => acc + curr.hourPrice, 0);

    return formatNumericValue(overtime);
  };

  return {
    getContractStatus,
    checkUnitMismatch,
    getUnitsTable,
    getWorkingHours,
    getOvertime,
  };
};
